import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import JsonLd from "@/components/feature/JsonLd";
import ArticleCard from "@/components/feature/ArticleCard";
import { newsCategories } from "@/mocks/newsCategories";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { formatHeat, getArticlesByCategory, getCategoryBySlug, getHotArticles } from "@/utils/news";

const allFilters = [{ slug: "all", name: "全部速报" }, ...newsCategories.map((c) => ({ slug: c.slug, name: c.name }))];

function CategoryPage() {
  const { slug = "all" } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [keyword, setKeyword] = useState(searchParams.get("q") ?? "");

  const category = getCategoryBySlug(slug);
  const isAll = slug === "all";
  const pageTitle = isAll ? "全部速报" : (category?.name ?? "未知分类");
  const pageDesc = isAll
    ? "瓜圈速报全部快讯集中在这里，按时间倒序同步突发、回应与后续进展，支持关键词搜索。"
    : (category?.desc ?? "这一类速报暂时没有找到。");

  const activeKeyword = searchParams.get("q") ?? "";

  useEffect(() => {
    setKeyword(activeKeyword);
  }, [activeKeyword]);

  const list = useMemo(() => {
    const base = getArticlesByCategory(slug);
    const key = activeKeyword.trim().toLowerCase();
    if (!key) {
      return base;
    }
    return base.filter((item) => {
      const pool = `${item.title}${item.summary}${item.tags.join("")}`.toLowerCase();
      return pool.includes(key);
    });
  }, [slug, activeKeyword]);

  const hotList = getHotArticles(5);

  useDocumentMeta({
    title: `${pageTitle} - 瓜圈速报`,
    description: pageDesc,
    keywords: `${pageTitle},瓜圈速报,瓜圈速报突发,第一手快讯,最新进展抢先看`,
    canonical: `https://zgdkf.cn/category/${slug}`,
  });

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "CollectionPage",
        name: `${pageTitle} - 瓜圈速报`,
        description: pageDesc,
        inLanguage: "zh-CN",
        url: `https://zgdkf.cn/category/${slug}`,
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://zgdkf.cn/" },
          { "@type": "ListItem", position: 2, name: pageTitle },
        ],
      },
      {
        "@type": "ItemList",
        itemListElement: list.slice(0, 10).map((item, idx) => ({
          "@type": "ListItem",
          position: idx + 1,
          name: item.title,
          url: `https://zgdkf.cn/article/${item.id}`,
        })),
      },
    ],
  };

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const next = new URLSearchParams(searchParams);
    if (keyword.trim()) {
      next.set("q", keyword.trim());
    } else {
      next.delete("q");
    }
    setSearchParams(next);
  };

  const clearSearch = () => {
    setKeyword("");
    const next = new URLSearchParams(searchParams);
    next.delete("q");
    setSearchParams(next);
  };

  return (
    <>
      <JsonLd data={jsonLd} />
      <SiteHeader />
      <main className="pt-24 md:pt-28">
        <section className="w-full px-4 md:px-10 pt-6">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: pageTitle }]} />
        </section>

        <section className="w-full px-4 md:px-10 pt-6 pb-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-8">
              <span className="inline-flex items-center gap-2 text-xs font-medium text-primary-600 tracking-[0.2em]">
                <span className="w-6 h-px bg-primary-500" />
                CATEGORY
              </span>
              <h1 className="mt-3 font-heading text-3xl md:text-5xl font-black text-foreground-950">{pageTitle}</h1>
              <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-600 max-w-2xl">{pageDesc}</p>
            </div>

            <div className="lg:col-span-4">
              <form
                onSubmit={submitSearch}
                className="flex items-center gap-2 bg-background-50 border border-background-300 rounded-lg px-3 py-2"
              >
                <span className="w-6 h-6 flex items-center justify-center text-foreground-400">
                  <i className="ri-search-line" />
                </span>
                <input
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  type="text"
                  name="q"
                  placeholder="搜标题、标签关键词"
                  className="flex-1 min-w-0 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none py-1"
                />
                <button
                  type="submit"
                  className="px-4 py-1.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
                >
                  搜索
                </button>
              </form>
            </div>
          </div>

          <div className="mt-8 flex items-center gap-2 flex-wrap">
            {allFilters.map((filter) => (
              <Link
                key={filter.slug}
                to={`/category/${filter.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                  filter.slug === slug
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-background-200"
                }`}
              >
                {filter.name}
              </Link>
            ))}
          </div>

          {activeKeyword && (
            <div className="mt-6 flex items-center gap-3 flex-wrap">
              <span className="text-sm text-foreground-600">
                关键词「<strong className="text-primary-600">{activeKeyword}</strong>」共找到 {list.length} 条速报
              </span>
              <button
                type="button"
                onClick={clearSearch}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-background-100 text-foreground-700 text-xs cursor-pointer hover:bg-background-200 transition-colors"
              >
                <span className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-close-line" />
                </span>
                清除搜索
              </button>
            </div>
          )}
        </section>

        <section className="w-full px-4 md:px-10 pb-16 md:pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-8">
              {list.length > 0 ? (
                <div className="flex flex-col gap-5">
                  {list.map((article, idx) => (
                    <ArticleCard key={article.id} article={article} index={idx} />
                  ))}
                </div>
              ) : (
                <div className="py-20 text-center border border-dashed border-background-300 rounded-lg">
                  <span className="inline-flex w-14 h-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                    <i className="ri-inbox-line text-2xl" />
                  </span>
                  <p className="mt-5 font-heading text-lg font-bold text-foreground-900">暂时没找到匹配的速报</p>
                  <p className="mt-2 text-sm text-foreground-600">换个关键词，或者去全部速报里翻翻看。</p>
                  <Link
                    to="/category/all"
                    className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
                  >
                    浏览全部速报
                  </Link>
                </div>
              )}
            </div>

            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-28 space-y-6">
                <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
                  <div className="px-5 py-4 border-b border-background-200 bg-background-100">
                    <h2 className="font-heading text-base font-bold text-foreground-950 flex items-center gap-2">
                      <span className="w-5 h-5 flex items-center justify-center text-primary-600">
                        <i className="ri-fire-fill" />
                      </span>
                      热瓜榜 · 实况
                    </h2>
                  </div>
                  <ol className="divide-y divide-background-200">
                    {hotList.map((item, idx) => (
                      <li key={item.id}>
                        <Link
                          to={`/article/${item.id}`}
                          className="flex items-start gap-4 px-5 py-4 hover:bg-background-100 transition-colors cursor-pointer"
                        >
                          <span
                            className={`shrink-0 w-7 h-7 flex items-center justify-center rounded-md font-heading text-sm font-black ${
                              idx < 3 ? "bg-primary-500 text-background-50" : "bg-background-200 text-foreground-700"
                            }`}
                          >
                            {idx + 1}
                          </span>
                          <span className="flex-1 min-w-0">
                            <span className="block text-sm font-medium text-foreground-900 leading-snug line-clamp-2">
                              {item.title}
                            </span>
                            <span className="mt-1.5 flex items-center gap-1 text-xs text-foreground-500">
                              <span className="w-3.5 h-3.5 flex items-center justify-center text-primary-500">
                                <i className="ri-fire-line" />
                              </span>
                              {formatHeat(item.heat)}
                            </span>
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ol>
                </div>

                <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
                  <div className="px-5 py-4 border-b border-background-200">
                    <h2 className="font-heading text-base font-bold text-foreground-950">速报分类</h2>
                  </div>
                  <ul className="divide-y divide-background-200">
                    {allFilters.map((filter) => (
                      <li key={filter.slug}>
                        <Link
                          to={`/category/${filter.slug}`}
                          className={`flex items-center justify-between gap-3 px-5 py-3.5 transition-colors cursor-pointer ${
                            filter.slug === slug
                              ? "bg-primary-50 text-primary-700"
                              : "hover:bg-background-100 text-foreground-700"
                          }`}
                        >
                          <span className="text-sm font-medium">{filter.name}</span>
                          <span className="w-4 h-4 flex items-center justify-center text-foreground-400">
                            <i className="ri-arrow-right-s-line" />
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </aside>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </>
  );
}

export default CategoryPage;